import type { Patient, Reference } from '@medplum/fhirtypes'
import type { DateString } from '@seen/core/dates'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCreateIfNoneExistsMutation } from '#src/hooks/useCreateIfNoneExistsMutation.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export const DAY_CENTER_DAY_NOTE_CODE = 'day-center-day-note'

export type StartDayCenterDayNoteSource =
  | { source: 'previous' }
  | { source: 'template'; html: string }

export interface StartDayCenterDayNoteResult {
  note: ExtendedObservation
  // False only on the `previous` path when the participant has no earlier note
  // at all; the note is still created, just empty. Always true for `template`.
  foundPrevious: boolean
}

// Creates the selected day's Day Center note on demand, seeded either from the
// participant's most recent earlier note or from a chosen note template.
//
// The lookback runs here, inside the mutation, rather than as a render-time
// query: the buttons are identical for every participant, so a page of rows
// costs no history requests until someone actually clicks one.
export function useStartDayCenterDayNote({
  participantReference,
  selectedDate,
}: {
  participantReference: Reference<Patient> | undefined
  selectedDate: DateString
}): UseMutationResult<
  StartDayCenterDayNoteResult,
  unknown,
  StartDayCenterDayNoteSource
> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const createIfNoneExists =
    useCreateIfNoneExistsMutation<ExtendedObservation>()
  const createNote = createIfNoneExists.mutateAsync

  const dayQuery = participantReference
    ? ExtendedObservation.searchQuery({
        subject: participantReference,
        code: DAY_CENTER_DAY_NOTE_CODE,
        date: selectedDate,
      })
    : undefined

  return useMutation({
    /* Record the started note somewhere the day search cannot erase.
     *
     * Invalidating the day's `searchOneExtended` entry is not enough, and
     * neither is writing the note into it: React Query's default `staleTime` is
     * 0, so every remount refetches that entry, and a search that misses a
     * just-written Observation replaces it with `null`. The row would read the
     * note correctly for one frame and then drop back to the start buttons for
     * a day that is already charted.
     *
     * `dayCenterDayNoteStarted` sits outside the `Observation` key space, so no
     * invalidation sweeps it and no refetch overwrites it. The row prefers the
     * live search when it has a note and falls back to this only to avoid
     * un-charting a day it already started. */
    onSuccess: ({ note }) => {
      queryClient.setQueryData(
        cacheKeys.dayCenterDayNoteStarted(
          participantReference?.reference,
          selectedDate
        ),
        note
      )
    },
    mutationFn: async (variables) => {
      if (!participantReference || !dayQuery) {
        throw new Error('A participant is required to start a Day Center note')
      }

      const previous =
        variables.source === 'previous'
          ? await medplum.searchOneResourceClass(
              ExtendedObservation,
              ExtendedObservation.queryMostRecentBefore({
                subject: participantReference,
                code: DAY_CENTER_DAY_NOTE_CODE,
                before: selectedDate,
              })
            )
          : null

      const value =
        variables.source === 'template'
          ? variables.html
          : (previous?.valueString ?? '')

      // Conditional create, so two clinicians starting the same participant's
      // note at once converge on one Observation instead of racing to two.
      const note = await createNote({
        instance: ExtendedObservation.buildFromString({
          subject: participantReference,
          code: DAY_CENTER_DAY_NOTE_CODE,
          value,
          effectiveDateTime: selectedDate,
        }),
        query: dayQuery,
      })

      return {
        note,
        // An earlier note with an empty body still counts as found — copying it
        // blank is correct, and "No previous" would be a lie.
        foundPrevious: variables.source === 'template' || !!previous,
      }
    },
  })
}
